import { useQuery } from '@do/seashell/graphql';
import {
  Box,
  Button,
  FlexboxGrid,
  mediaQueries,
  VisuallyHidden,
  style,
  TabbedContent,
  TabList,
  Tab,
  TabPanel,
  TabPanels
} from '@do/walrus';
import _concat from 'lodash/concat';
import _get from 'lodash/get';
import dynamic from 'next/dynamic';
import * as React from 'react';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import GetSaasAppBySlugQuery from '../../graphql/queries/GetSaasAppBySlug.gql';
import CategoriesQuery from '../../graphql/queries/categories.gql';
import AddOnsByAppQuery from '../../graphql/queries/getAddonsByApp.gql';
import {
  getAddonSlug,
  getAppId,
  getCategory,
  getCreated,
  getDeveloperName,
  getIcon,
  getImageId,
  getName,
  getOsVersion,
  getSafeName,
  getSummary,
  getTags,
  getType,
  getVersion
} from '../../selectors';
import {
  isAIModel,
  isDroplet,
  isKubernetes,
  isSaasAffiliate
} from '../../utils';
import {
  ALGOLIA_TRACK_ADD_EVENT,
  MP_CATALOG_CREATE_DROPLET_EVENT_PREFIX,
  MP_CATALOG_CREATE_K8S_EVENT_PREFIX,
  MP_CATALOG_CREATE_SAAS_AFFILIATE_EVENT_PREFIX
} from '../../utils/constants';
import { slugify } from '../../utils/slug-helpers';
import { trackGTMEvent, trackProductSearch } from '../../utils/tracking';
import Breadcrumbs from '../Breadcrumbs';
import { NodeMongoBanner } from '../NodeMongoBanner';
import { MONGO_SAFENAME, NODE_SAFENAME } from '../NodeMongoBanner/constants';
import OneClickResource from '../OneClickResource/OneClickResource';
import { HitProps } from '../Search/onSearchSelect';
import { FeaturedTutorialSection } from '../Tutorials';
import { TUTORIALS_DATA } from '../Tutorials/constants';
import copy from '../copy';
import AdditionalLinks from './AdditionalLinks';
import ApiCreationSection from './ApiCreationSection';
import AppScriptsSection from './AppScriptsSection';
import Description from './Description';
import DetailAccordion from './DetailAccordion';
import GettingStarted, { GETTING_STARTED_HASH } from './GettingStarted';
import LicenseAddonsPlansTab from './LicenseAddonsPlansTab';
import { SelectedPlanCard } from './SelectedPlanCard';
import SoftwareIncluded from './SoftwareIncluded';
import SupportDetails from './SupportDetails';

const Search = dynamic(() => import('../Search'));

const PENDO_ID = 'create-1-click-app-button';

const AppDetailGrid = styled(FlexboxGrid)`
  margin-top: ${style.vars.space['2']};
`;

const CreateButtonContainer = styled.div`
  ${mediaQueries.largeMin} {
    padding: ${style.vars.space['8']} 0 ${style.vars.space['5']};
  }
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  width: 100%;
`;

const CreateButton = styled(Button).attrs({
  className: PENDO_ID,
  'data-pendo-id': PENDO_ID
})`
  ${mediaQueries.largeMax} {
    width: 100%;
  }
  span {
    display: block;
    width: 100%;
    text-overflow: ellipsis;
    overflow: hidden;
  }
`;

const OneClickResourceContainer = styled.div`
  position: relative;
  margin: ${style.vars.space['8']} 0 ${style.vars.space['3']};
  ${mediaQueries.mediumMax} {
    margin: ${style.vars.space['8']} 0;
  }
`;

const RightColumn = styled.div<{ displaysAd?: boolean }>`
  && {
    border-top: 1px solid ${style.colors.shadesOfGrey['4']};
    padding-top: ${style.vars.space['7']};
    margin-top: ${style.vars.space['7']};
    color: ${style.colors.shadesOfGrey['8']};

    p {
      margin-top: ${style.vars.space['1']};
    }

    ${mediaQueries.largeMin} {
      position: ${(props) => (props.displaysAd ? 'auto' : 'sticky')};
      top: 0;
      border: 0 none;
      padding-left: ${style.vars.space['8']};
      padding-top: ${style.vars.space['3']};
      margin-top: 0;
    }

    ${mediaQueries.xlargeMin} {
      max-width: 520px;
    }
  }
`;

const HideOnMobile = styled.div`
  ${mediaQueries.mediumMax} {
    display: none;
  }
`;

const HideOnDesktop = styled.div`
  display: none;

  ${mediaQueries.mediumMax} {
    display: block;
  }
`;

interface TrackingProps {
  developer?: string;
  resource?: string | number;
  safeName?: string;
  type?: string;
}

interface Props {
  app: any;
  createButtonUrl?: string;
  isPreview?: boolean;
  onClickCreateButton?: (props: TrackingProps, e: React.MouseEvent) => void;
  showSearch?: boolean;
  isInIframe?: boolean;
  hit?: HitProps;
}

function getCategoryFromReferrer(app): string | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const referrer = document.referrer;
  const referrerPath = referrer?.split(document.location.origin)?.[1];
  const referrerCategory = referrerPath?.split('/category/')?.[1];

  const tags = getTags(app) ?? [];
  const matchingCategory = tags?.find(
    (tag: string) => tag.toLowerCase() === referrerCategory?.toLowerCase()
  );

  return matchingCategory || getCategory(app);
}

function isAppStaffPick(categoriesData, name) {
  const allSolutionsData = _get(
    categoriesData,
    'getMarketplaceCategoryPage.categories[0]'
  );

  const allSolutionsStaffPicks = _concat(
    _get(allSolutionsData, 'staff_pick_1'),
    _get(allSolutionsData, 'staff_pick_2'),
    _get(allSolutionsData, 'staff_pick_3'),
    _get(allSolutionsData, 'staff_pick_4')
  );

  return allSolutionsStaffPicks.some(
    (staffPick) => _get(staffPick, 'name') === name
  );
}

function getCreateButtonCopy(type: string, name: string) {
  if (isKubernetes(type)) {
    return copy.installApp;
  } else if (isDroplet(type)) {
    return `${copy.create} ${name} ${copy.droplet}`;
  } else if (isSaasAffiliate(type)) {
    return `${copy.signUpFor} ${name}`;
  } else if (isAIModel(type)) {
    return `${copy.create} ${name}`;
  }
}

export const getTrackingPrefix = (type: string) => {
  if (isKubernetes(type)) {
    return MP_CATALOG_CREATE_K8S_EVENT_PREFIX;
  } else if (isDroplet(type)) {
    return MP_CATALOG_CREATE_DROPLET_EVENT_PREFIX;
  } else if (isSaasAffiliate(type)) {
    return MP_CATALOG_CREATE_SAAS_AFFILIATE_EVENT_PREFIX;
  }
};

const injectLicenseAddonToCreateButton = (
  url: string,
  saasLicenseAddon: string
) => {
  const encodedLicenseAddonPlan = encodeURIComponent(
    `&saasLicenseAddon=${saasLicenseAddon}`
  );

  const splitUrl = url.split('&activation_redirect');
  if (splitUrl.length !== 2) {
    return url;
  }
  return `${splitUrl[0]}&saasLicenseAddon=${saasLicenseAddon}&activation_redirect${splitUrl[1]}${encodedLicenseAddonPlan}`;
};

const AppDetail: React.FC<Props> = ({
  app,
  createButtonUrl = '#',
  isPreview,
  onClickCreateButton,
  showSearch,
  hit,
  isInIframe
}) => {
  const name = getName(app);
  const safeName = getSafeName(app);
  const summary = getSummary(app);
  const icon = getIcon(app);
  const developerName = getDeveloperName(app);
  const category = getCategoryFromReferrer(app);
  const type = getType(app);
  const resource =
    isKubernetes(type) && getAddonSlug(app)
      ? getAddonSlug(app)
      : getImageId(app);

  const { data: categoriesData } = useQuery(CategoriesQuery, {
    fetchPolicy: 'no-cache'
  });

  const { data: addOnsByAppData } = useQuery(AddOnsByAppQuery, {
    variables: {
      getAddonsByAppRequestPublic: {
        app_uuid: app?.app_id
      }
    },
    skip: !app?.app_id,
    fetchPolicy: 'no-cache'
  });

  const licenseAddon = addOnsByAppData?.GetPublicAddonsByApp?.addons?.[0];

  const { data: relatedAddonData } = useQuery(GetSaasAppBySlugQuery, {
    variables: {
      payload: { app_slug: licenseAddon?.addon_slug }
    },
    skip: !licenseAddon?.addon_slug,
    fetchPolicy: 'no-cache'
  });

  const relatedAddonPlans =
    relatedAddonData?.GetSaasAppBySlug?.app?.plans ?? [];

  const defaultAddonPlan =
    relatedAddonPlans?.find((plan) => plan.by_default)?.slug ||
    relatedAddonPlans?.[0]?.slug ||
    null;

  const [selectedPlan, setSelectedPlan] = React.useState(null);

  const containerRef = useRef<HTMLDivElement>();

  const createButtonCopy = getCreateButtonCopy(type, name);

  const isStaffPick = isAppStaffPick(categoriesData, name);

  const isNodeOrMongo =
    safeName === NODE_SAFENAME || safeName === MONGO_SAFENAME;

  const tutorialsData = TUTORIALS_DATA[safeName];
  const firstFeaturedTutorial = tutorialsData?.featuredTutorials?.[0];

  const pageProps = {
    action: 'view',
    name: `${type} app detail page`,
    pageName: `${type} app detail`
  };

  useEffect(() => {
    // set selectedPlan on initial load for add-ons with plans
    if (selectedPlan === null && defaultAddonPlan !== selectedPlan) {
      setSelectedPlan(defaultAddonPlan);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [defaultAddonPlan, selectedPlan]);

  useEffect(() => {
    const container = containerRef?.current;

    if (!isInIframe) {
      trackGTMEvent(getTrackingPrefix(type), 'start', pageProps);
      container?.addEventListener?.('click', () =>
        trackGTMEvent(getTrackingPrefix(type), 'click', {
          ...pageProps,
          action: 'click'
        })
      );
    }

    return () => {
      container?.removeEventListener?.('click', () =>
        trackGTMEvent(getTrackingPrefix(type), 'click', {
          ...pageProps,
          action: 'click'
        })
      );
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    window?.Prism?.highlightAll?.();
  }, []);

  useEffect(() => {
    if (isInIframe) {
      return;
    }
    const hash = window?.location?.hash;

    if (hash === `#${GETTING_STARTED_HASH}`) {
      const el = document.querySelector(hash);
      el?.scrollIntoView(true);
    }
  }, [isInIframe]);

  const createButtonUrlWithPlan = selectedPlan
    ? `${injectLicenseAddonToCreateButton(createButtonUrl, selectedPlan)}`
    : createButtonUrl;

  const createButtonElement = (
    <CreateButton
      url={createButtonUrlWithPlan}
      external
      data-tracking-id={
        isKubernetes(type) && 'doks-install-add-on-from-marketplace'
      }
      data-test-id={`create-${name}-${type}-button`}
      onClick={(event) => {
        if (hit) {
          trackProductSearch(ALGOLIA_TRACK_ADD_EVENT, 'conversion', hit);
        }

        onClickCreateButton?.(
          {
            developer: developerName,
            type,
            resource,
            safeName
          },
          event
        );
      }}
    >
      {createButtonCopy}
    </CreateButton>
  );

  const [tabIndex, setTabIndex] = React.useState(0);

  const searchElement = showSearch && (
    <Search placeholder={copy.search.placeholder} />
  );

  return (
    <div ref={containerRef}>
      {isInIframe ? (
        <DetailAccordion
          app={app}
          tutorialsData={tutorialsData || null}
          onClickCreateButton={onClickCreateButton}
          createButtonUrl={createButtonUrlWithPlan}
          isStaffPick={isStaffPick}
        />
      ) : (
        <AppDetailGrid>
          <FlexboxGrid.Col sm={12} lg={7}>
            <HideOnDesktop>{searchElement}</HideOnDesktop>
            {category && (
              <Breadcrumbs
                hrefs={['/category/[slug]', '/category/[slug]']}
                as={['/category/all', `/category/${slugify(category)}`]}
                text={[copy.breadCrumbs.allCategories, category]}
                openInNewTab={isPreview}
              />
            )}
            <OneClickResourceContainer>
              <VisuallyHidden>
                <h1>{name}</h1>
              </VisuallyHidden>
              <OneClickResource
                isHorizontal={true}
                name={name}
                appID={getAppId(app)}
                slug={safeName}
                icon={icon}
                version={getVersion(app)}
                osVersion={getOsVersion(app)}
                developer={developerName}
                created={getCreated(app)}
                summary={summary}
                category={category}
                type={type}
                isStaffPick={isStaffPick}
              />
            </OneClickResourceContainer>
            {licenseAddon ? (
              <TabbedContent
                defaultIndex={0}
                index={tabIndex}
                onChange={(index) => setTabIndex(index)}
              >
                <TabList>
                  <Tab>Overview</Tab>
                  {relatedAddonPlans.length > 0 && <Tab>Plans</Tab>}
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <HideOnDesktop>{createButtonElement}</HideOnDesktop>
                    {isNodeOrMongo && <NodeMongoBanner />}
                    <Description app={app} />
                    <SoftwareIncluded app={app} />
                    <GettingStarted app={app} />
                    <AppScriptsSection appSlug={safeName} appName={name} />
                    <ApiCreationSection app={app} />
                  </TabPanel>
                  <TabPanel>
                    {relatedAddonData && relatedAddonPlans.length > 0 && (
                      <LicenseAddonsPlansTab
                        addon={licenseAddon}
                        selectedPlan={selectedPlan}
                        setSelectedPlan={setSelectedPlan}
                        plans={relatedAddonPlans}
                        isInIframe={isInIframe}
                      />
                    )}
                  </TabPanel>
                </TabPanels>
              </TabbedContent>
            ) : (
              <>
                <HideOnDesktop>{createButtonElement}</HideOnDesktop>
                {isNodeOrMongo && <NodeMongoBanner />}
                <Description app={app} />
                <SoftwareIncluded app={app} />
                <GettingStarted app={app} />
                <AppScriptsSection appSlug={safeName} appName={name} />
                <ApiCreationSection app={app} />
              </>
            )}
          </FlexboxGrid.Col>
          <FlexboxGrid.Col sm={12} lg={5}>
            <RightColumn>
              <HideOnMobile>
                {searchElement}
                <CreateButtonContainer>
                  {createButtonElement}
                </CreateButtonContainer>
              </HideOnMobile>

              {tutorialsData && (
                <Box mt={4}>
                  <FeaturedTutorialSection
                    name={name}
                    tutorial={firstFeaturedTutorial}
                    tutorialsLink={tutorialsData.tutorialsLink}
                  />
                </Box>
              )}
              {relatedAddonPlans.length > 0 && (
                <SelectedPlanCard
                  selectedPlanSlug={selectedPlan}
                  plans={relatedAddonPlans}
                  setTabIndex={setTabIndex}
                  isPlanTab={tabIndex === 1}
                />
              )}
              <SupportDetails app={app} />
              <AdditionalLinks app={app} />
            </RightColumn>
          </FlexboxGrid.Col>
        </AppDetailGrid>
      )}
    </div>
  );
};

export default AppDetail;
